import React, { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import MinimalLayout from '../components/layout/MinimalLayout';
import { 
  UserCircleIcon, 
  MagnifyingGlassIcon,
  PencilIcon,
  CheckIcon,
  XMarkIcon,
  AcademicCapIcon
} from '@heroicons/react/24/solid';

interface StudentProfileData {
  email: string;
  instrument: string;
  learnerType: 'learner' | 'guardian';
  learnerFirstName?: string;
  age: string;
  goals: string;
  skillLevel: string;
  genres: string[];
  previousLessons: string;
  lessonFrequency: string;
  lessonLocation: string[];
}

const StudentProfilePage: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  
  // Get student data from navigation state
  const studentData = location.state?.studentData as StudentProfileData;
  const [showSuccessMessage, setShowSuccessMessage] = useState(true);

  // Log successful profile creation
  console.log('Student profile page loaded with data:', studentData);

  // If no student data, redirect back
  if (!studentData) {
    console.warn('No student data found, redirecting to instrument selection');
    navigate('/instrument-selection');
    return null;
  }

  const isGuardian = studentData.learnerType === 'guardian';
  const displayName = studentData.learnerFirstName || 'Student';

  const handleBrowseTeachers = () => {
    console.log('Navigating to teacher browse with preferences:', studentData);
    // TODO: Navigate to teacher browse page with student preferences
    navigate('/browse-teachers', { 
      state: { 
        studentPreferences: studentData 
      } 
    });
  };

  const handleEditProfile = () => {
    console.log('Navigating to edit student profile');
    // TODO: Navigate to edit profile page
    navigate('/student/edit-profile', { 
      state: { 
        studentData 
      } 
    });
  };

  return (
    <MinimalLayout noPadding>
      <div className="min-h-screen bg-gradient-to-b from-blue-50 to-white py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-3xl mx-auto">
          
          {/* Success Message */}
          {showSuccessMessage && (
            <div className="mb-8 bg-green-500/20 border border-green-500/50 rounded-lg p-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <CheckIcon className="h-6 w-6 text-green-400" />
                  <div>
                    <h3 className="text-green-700 font-semibold">Account Created Successfully!</h3>
                    <p className="text-green-600 text-sm">
                      {isGuardian 
                        ? `${displayName}'s profile is ready. You can now browse teachers and book lessons.`
                        : "Your profile is ready. You can now browse teachers and book lessons."
                      }
                    </p>
                  </div>
                </div>
                <button 
                  onClick={() => setShowSuccessMessage(false)}
                  className="text-green-400 hover:text-green-300"
                >
                  <XMarkIcon className="h-5 w-5" />
                </button>
              </div>
            </div>
          )}

          {/* Profile Header */}
          <div className="bg-white rounded-xl shadow-lg overflow-hidden">
            
            {/* Profile Picture Section */}
            <div className="bg-gradient-to-r from-blue-500 to-blue-600 px-8 py-12">
              <div className="flex flex-col items-center">
                <div className="relative mb-6">
                  <UserCircleIcon className="w-32 h-32 text-white" />
                  
                  {/* Profile decoration */}
                  <div className="absolute bottom-0 right-0 bg-white p-3 rounded-full shadow-lg">
                    <AcademicCapIcon className="w-6 h-6 text-blue-500" />
                  </div>
                </div>
                
                <h1 className="text-3xl font-bold text-white text-center">
                  {isGuardian ? `${displayName}'s Profile` : displayName}
                </h1>
                <p className="text-blue-100 text-lg mt-2">
                  {studentData.instrument} Student
                </p>
                <p className="text-blue-100 text-sm mt-1">
                  {studentData.skillLevel} Level
                </p>
              </div>
            </div>

            {/* Profile Details */}
            <div className="p-8">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                
                {/* Learning Information */}
                <div>
                  <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center">
                    <AcademicCapIcon className="w-5 h-5 mr-2 text-blue-500" />
                    Learning Details
                  </h3>
                  <div className="space-y-3">
                    <div>
                      <span className="text-gray-500 text-sm">Instrument:</span>
                      <p className="text-gray-800 font-medium">{studentData.instrument}</p>
                    </div>
                    <div>
                      <span className="text-gray-500 text-sm">Age:</span>
                      <p className="text-gray-800">{studentData.age}</p>
                    </div>
                    <div>
                      <span className="text-gray-500 text-sm">Current Level:</span>
                      <p className="text-gray-800">{studentData.skillLevel}</p>
                    </div>
                    <div>
                      <span className="text-gray-500 text-sm">Previous Lessons:</span>
                      <p className="text-gray-800">{studentData.previousLessons}</p>
                    </div>
                  </div>
                </div>

                {/* Preferences */}
                <div>
                  <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center">
                    <PencilIcon className="w-5 h-5 mr-2 text-blue-500" />
                    Preferences
                  </h3>
                  <div className="space-y-3">
                    <div>
                      <span className="text-gray-500 text-sm">Contact Email:</span>
                      <p className="text-gray-800">{studentData.email}</p>
                    </div>
                    <div>
                      <span className="text-gray-500 text-sm">Lesson Frequency:</span>
                      <p className="text-gray-800">{studentData.lessonFrequency}</p>
                    </div>
                    <div>
                      <span className="text-gray-500 text-sm">Preferred Locations:</span>
                      <p className="text-gray-800">{studentData.lessonLocation.join(', ')}</p>
                    </div>
                  </div>
                </div>

                {/* Goals and Interests */}
                <div className="md:col-span-2">
                  <h3 className="text-lg font-semibold text-gray-800 mb-4">Goals & Interests</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <span className="text-gray-500 text-sm">Learning Goals:</span>
                      <p className="text-gray-800 mt-1 leading-relaxed">{studentData.goals}</p>
                    </div>
                    
                    <div>
                      <span className="text-gray-500 text-sm">Interested Genres:</span>
                      <div className="flex flex-wrap gap-2 mt-2">
                        {(studentData.genres || []).slice(0, 8).map((genre, index) => (
                          <span 
                            key={index}
                            className="bg-blue-100 text-blue-700 px-3 py-1 rounded-full text-sm"
                          >
                            {genre}
                          </span>
                        ))}
                        {(studentData.genres || []).length > 8 && (
                          <span className="text-gray-500 text-sm px-3 py-1">
                            +{(studentData.genres || []).length - 8} more
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="mt-8 pt-6 border-t border-gray-200">
                <div className="flex flex-col sm:flex-row gap-4">
                  <button
                    onClick={handleBrowseTeachers}
                    className="flex-1 bg-blue-500 hover:bg-blue-600 text-white font-semibold py-3 px-6 rounded-lg transition-colors flex items-center justify-center space-x-2"
                  >
                    <MagnifyingGlassIcon className="w-5 h-5" />
                    <span>Browse Teachers</span>
                  </button>
                  <button
                    onClick={handleEditProfile}
                    className="flex-1 border border-gray-300 text-gray-700 hover:text-gray-800 hover:border-gray-400 font-semibold py-3 px-6 rounded-lg transition-colors flex items-center justify-center space-x-2"
                  >
                    <PencilIcon className="w-5 h-5" />
                    <span>Edit Profile</span>
                  </button>
                </div>
                
                <div className="mt-6 text-center">
                  <p className="text-gray-500 text-sm mb-2">
                    Ready to start learning? Browse our qualified teachers and book your first lesson.
                  </p>
                  <div className="flex justify-center space-x-6 text-sm text-gray-400">
                    <span>✓ Verified Teachers</span>
                    <span>✓ Flexible Scheduling</span>
                    <span>✓ Multiple Lesson Formats</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </MinimalLayout>
  );
};

export default StudentProfilePage; 